export interface InstallationListFilters {
  search: string;
  types: string[];
  statuses: string[];
  technician: string;
  from: string;
  to: string;
}
const TYPES = ["installation", "breakdown", "maintenance"];
const STATUSES = ["pending", "in_progress", "completed", "cancelled"];
const KEYS = ["q", "type", "status", "tech", "from", "to"];
function date(value: string | null): string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return "";
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value ? value : "";
}
export function parseInstallationFilters(search: string): InstallationListFilters {
  const p = new URLSearchParams(search);
  const values = (key: string, allowed: string[]) => allowed.filter(v => (p.get(key) ?? "").split(",").includes(v));
  const tech = p.get("tech") ?? "";
  let from = date(p.get("from")), to = date(p.get("to"));
  if (from && to && from > to) { from = ""; to = ""; }
  return {
    search: (p.get("q") ?? "").replace(/[\u0000-\u001f\u007f]/g, "").slice(0, 200),
    types: values("type", TYPES), statuses: values("status", STATUSES),
    technician: /^[1-9]\d*$/.test(tech) && Number.isSafeInteger(Number(tech)) ? tech : "",
    from, to,
  };
}
export function installationListPath(filters: InstallationListFilters, currentSearch = ""): string {
  const p = new URLSearchParams(currentSearch);
  KEYS.forEach(k => p.delete(k));
  if (filters.search) p.set("q", filters.search);
  if (filters.types.length) p.set("type", filters.types.join(","));
  if (filters.statuses.length) p.set("status", filters.statuses.join(","));
  if (filters.technician) p.set("tech", filters.technician);
  if (filters.from) p.set("from", filters.from);
  if (filters.to) p.set("to", filters.to);
  return `/installations${p.size ? `?${p}` : ""}`;
}
export function installationDetailPath(id: number, filters: InstallationListFilters, detailSearch = ""): string {
  const p = new URLSearchParams(detailSearch);
  const back = installationListPath(filters);
  if (back !== "/installations") p.set("returnTo", back);
  return `/installations/${id}${p.size ? `?${p}` : ""}`;
}
/** Accept only a root-relative route that resolves on the current site. */
export function safeSameOriginRoute(target: string | null | undefined): string | null {
  if (!target || !target.startsWith("/") || target.startsWith("//") || target.includes("\\") || /[\u0000-\u001f\u007f]/.test(target)) {
    return null;
  }
  try {
    const base = "https://ods-internal.invalid";
    const resolved = new URL(target, base);
    if (resolved.origin !== base) return null;
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  } catch {
    return null;
  }
}

export function installationEditorReturnPath(search: string): string | null {
  return safeSameOriginRoute(new URLSearchParams(search).get("returnTo"));
}

export function installationEditorClosePath(search: string): string | null {
  const returnPath = installationEditorReturnPath(search);
  if (returnPath) return returnPath;

  const params = new URLSearchParams(search);
  if (!params.has("edit")) return null;
  params.delete("edit");
  return `/installations${params.size ? `?${params.toString()}` : ""}`;
}

// Only reconstruct a local installations list. Never use an arbitrary return URL.
export function installationListReturnPath(search: string): string {
  const target = new URLSearchParams(search).get("returnTo") ?? "";
  if (!target.startsWith("/installations?")) return "/installations";
  return installationListPath(parseInstallationFilters(target.slice("/installations?".length)));
}
